import React, { useCallback, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@mui/material';
import { Order } from '../orders.types';
import { getOrderTrackingEvidence, OrderTrackingEvidence, TrackingEvent } from '../orders.api';

interface ShippingEvidenceDialogProps {
    open: boolean;
    order: Order | null;
    onClose: () => void;
}

const formatCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        });
    } catch {
        return dateStr;
    }
};

const formatLocation = (location: TrackingEvent['location']): string => {
    if (!location) return '—';
    const parts = [location.city, location.state, location.zip, location.country].filter(Boolean);
    return parts.join(', ') || '—';
};

const statusBadgeColor = (status: string | null): string => {
    if (!status) return '#6b7280';
    const s = status.toUpperCase();
    if (s === 'DELIVERED') return '#16a34a';
    if (s === 'TRANSIT') return '#2563eb';
    if (s === 'PRE_TRANSIT') return '#7c3aed';
    if (s === 'RETURNED') return '#ea580c';
    if (s === 'FAILURE') return '#dc2626';
    return '#6b7280';
};

interface EvidenceDocumentProps {
    evidence: OrderTrackingEvidence;
}

const EvidenceDocument: React.FC<EvidenceDocumentProps> = ({ evidence }) => {
    const { order, tracking } = evidence;

    const buyerName = order.buyer_org_name ?? order.buyer_athlete_name ?? '—';
    const buyerEmail = order.buyer_athlete_email ?? '—';

    return (
        <Box
            id="shipping-evidence-printable"
            sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontSize: '12px',
                color: '#111',
                p: 3,
                maxWidth: '800px',
                mx: 'auto',
                '@media print': {
                    p: 0,
                    maxWidth: 'none',
                    fontSize: '11px',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                    pb: 2,
                    borderBottom: '2px solid #111',
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, fontSize: '18px', color: '#111' }}
                    >
                        Shipping Evidence — Credit Card Dispute
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#555', mt: 0.5 }}>
                        Generated: {formatDate(evidence.captured_at)}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '11px', color: '#555' }}>
                        Order ID: {order.uuid}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#555' }}>
                        Order Date: {formatDate(order.created_at)}
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-block',
                            mt: 0.5,
                            px: 1,
                            py: 0.3,
                            borderRadius: '4px',
                            backgroundColor: tracking.is_delivered ? '#dcfce7' : '#fef3c7',
                            color: tracking.is_delivered ? '#16a34a' : '#92400e',
                            fontSize: '11px',
                            fontWeight: 700,
                            border: `1px solid ${tracking.is_delivered ? '#86efac' : '#fcd34d'}`,
                        }}
                    >
                        {tracking.is_delivered
                            ? '✓ DELIVERED'
                            : tracking.current_status ?? 'IN TRANSIT'}
                    </Box>
                </Box>
            </Box>

            {/* Two-column layout: Order Details + Shipping Address */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                {/* Seller / Buyer */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#555',
                            mb: 0.5,
                        }}
                    >
                        Merchant (Seller)
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                        {order.seller_org_name ?? '—'}
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: '#555',
                                mb: 0.5,
                            }}
                        >
                            Customer (Buyer)
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                            {buyerName}
                        </Typography>
                        {order.buyer_athlete_email && (
                            <Typography sx={{ fontSize: '11px', color: '#555' }}>
                                {buyerEmail}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Shipping address */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#555',
                            mb: 0.5,
                        }}
                    >
                        Ship-To Address
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{buyerName}</Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                        {order.shipping_address.line1}
                    </Typography>
                    {order.shipping_address.line2 && (
                        <Typography sx={{ fontSize: '12px' }}>
                            {order.shipping_address.line2}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '12px' }}>
                        {order.shipping_address.city}, {order.shipping_address.state}{' '}
                        {order.shipping_address.zip}
                    </Typography>
                </Box>

                {/* Financials */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#555',
                            mb: 0.5,
                        }}
                    >
                        Order Totals
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', color: '#555' }}>Subtotal</Typography>
                        <Typography sx={{ fontSize: '12px' }}>
                            {formatCents(
                                order.total_cents - order.tax_cents - order.shipping_cents,
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', color: '#555' }}>Tax</Typography>
                        <Typography sx={{ fontSize: '12px' }}>
                            {formatCents(order.tax_cents)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', color: '#555' }}>Shipping</Typography>
                        <Typography sx={{ fontSize: '12px' }}>
                            {formatCents(order.shipping_cents)}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                            Total Charged
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                            {formatCents(order.total_cents)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Line items */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: '#555',
                        mb: 0.5,
                    }}
                >
                    Items Ordered
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 80px 100px 100px',
                            backgroundColor: '#f3f4f6',
                            px: 1.5,
                            py: 0.75,
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#374151',
                        }}
                    >
                        <span>Description</span>
                        <span style={{ textAlign: 'center' }}>Qty</span>
                        <span style={{ textAlign: 'right' }}>Unit Price</span>
                        <span style={{ textAlign: 'right' }}>Subtotal</span>
                    </Box>
                    {order.line_items.map((item, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 80px 100px 100px',
                                px: 1.5,
                                py: 0.75,
                                fontSize: '12px',
                                borderTop: '1px solid #e5e7eb',
                                backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb',
                            }}
                        >
                            <span>{item.description}</span>
                            <span style={{ textAlign: 'center' }}>{item.quantity}</span>
                            <span style={{ textAlign: 'right' }}>
                                {formatCents(item.unit_amount_cents)}
                            </span>
                            <span style={{ textAlign: 'right' }}>
                                {formatCents(item.amount_subtotal_cents)}
                            </span>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Tracking summary */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: '#555',
                        mb: 0.5,
                    }}
                >
                    Shipment Information
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        p: 1.5,
                        backgroundColor: '#f9fafb',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Carrier
                            </Typography>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                {tracking.carrier.toUpperCase()}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Tracking Number
                            </Typography>
                            <Typography
                                sx={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' }}
                            >
                                {tracking.tracking_number}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Status
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: statusBadgeColor(tracking.current_status),
                                }}
                            >
                                {tracking.current_status ?? '—'}
                            </Typography>
                        </Box>
                        {tracking.is_delivered && tracking.delivery_date && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Delivery Confirmed
                                </Typography>
                                <Typography
                                    sx={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}
                                >
                                    {formatDate(tracking.delivery_date)}
                                </Typography>
                            </Box>
                        )}
                        {!tracking.is_delivered && tracking.eta && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Estimated Delivery
                                </Typography>
                                <Typography sx={{ fontSize: '12px' }}>
                                    {formatDate(tracking.eta)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {tracking.current_status_details && (
                        <Typography
                            sx={{ fontSize: '11px', color: '#374151', mt: 1, fontStyle: 'italic' }}
                        >
                            {tracking.current_status_details}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Tracking events */}
            {tracking.events.length > 0 && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#555',
                            mb: 0.5,
                        }}
                    >
                        Full Tracking History ({tracking.events.length} events)
                    </Typography>
                    <Box
                        sx={{
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '160px 120px 1fr',
                                backgroundColor: '#f3f4f6',
                                px: 1.5,
                                py: 0.75,
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#374151',
                            }}
                        >
                            <span>Date / Time</span>
                            <span>Location</span>
                            <span>Details</span>
                        </Box>
                        {tracking.events.map((event, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '160px 120px 1fr',
                                    px: 1.5,
                                    py: 0.75,
                                    fontSize: '11px',
                                    borderTop: '1px solid #e5e7eb',
                                    backgroundColor:
                                        event.status === 'DELIVERED'
                                            ? '#f0fdf4'
                                            : idx % 2 === 0
                                            ? '#fff'
                                            : '#f9fafb',
                                }}
                            >
                                <span style={{ color: '#374151' }}>
                                    {formatDate(event.status_date)}
                                </span>
                                <span style={{ color: '#374151' }}>
                                    {formatLocation(event.location)}
                                </span>
                                <span
                                    style={{
                                        color: statusBadgeColor(event.status),
                                        fontWeight: event.status === 'DELIVERED' ? 700 : 400,
                                    }}
                                >
                                    {event.status_details ?? event.status ?? '—'}
                                </span>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Legal footer */}
            <Box
                sx={{
                    mt: 3,
                    pt: 1.5,
                    borderTop: '1px solid #d1d5db',
                    fontSize: '10px',
                    color: '#6b7280',
                }}
            >
                <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>
                    This document was generated from carrier tracking data provided by Shippo/UPS
                    and is intended for use as evidence in credit card dispute resolution. Tracking
                    data captured: {formatDate(evidence.captured_at)}.
                    {tracking.is_delivered
                        ? ' Package delivery has been confirmed by the carrier.'
                        : ' Package delivery has not yet been confirmed.'}
                </Typography>
            </Box>
        </Box>
    );
};

const ShippingEvidenceDialog: React.FC<ShippingEvidenceDialogProps> = ({
    open,
    order,
    onClose,
}) => {
    const [evidence, setEvidence] = useState<OrderTrackingEvidence | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const printRef = useRef<HTMLDivElement>(null);

    const fetchEvidence = useCallback(async () => {
        if (!order) return;
        setLoading(true);
        setError(null);
        try {
            const response = await getOrderTrackingEvidence(order.uuid);
            setEvidence(response.data.data);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data?.message;
            let msg = serverMsg;
            if (!msg) {
                if (status === 502 || status === 503) {
                    msg =
                        'Shippo could not find tracking data for this shipment. This usually means the tracking number is too old or was never registered with the carrier. Evidence can only be retrieved for recent shipments.';
                } else if (status === 404) {
                    msg = 'No tracking number is on record for this order.';
                } else {
                    msg =
                        'Failed to load tracking evidence. Please try again or check that the tracking number is valid.';
                }
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [order]);

    // Fetch when dialog opens
    React.useEffect(() => {
        if (open && order) {
            setEvidence(null);
            setError(null);
            fetchEvidence();
        }
    }, [open, order, fetchEvidence]);

    const handlePrint = () => {
        const printContents = document.getElementById('shipping-evidence-printable');
        if (!printContents) return;

        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) return;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Shipping Evidence — ${order?.uuid ?? ''}</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: "Helvetica Neue", Arial, sans-serif; font-size: 11px; color: #111; padding: 24px; }
                    @media print {
                        body { padding: 0; }
                        @page { margin: 15mm; }
                    }
                </style>
            </head>
            <body>
                ${printContents.innerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Shipping Evidence
                {order?.shipping_tracking_number && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '13px',
                            color: 'text.secondary',
                            ml: 1,
                            fontFamily: 'monospace',
                        }}
                    >
                        #{order.shipping_tracking_number}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, minHeight: '400px' }}>
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 8,
                        }}
                    >
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Fetching tracking data from Shippo…</Typography>
                    </Box>
                )}
                {error && !loading && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error" sx={{ alignItems: 'flex-start' }}>
                            <Typography sx={{ fontSize: '14px', mb: 1 }}>{error}</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={fetchEvidence}
                            >
                                Retry
                            </Button>
                        </Alert>
                    </Box>
                )}
                {evidence && !loading && (
                    <Box ref={printRef}>
                        <EvidenceDocument evidence={evidence} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {evidence && (
                    <Button variant="contained" onClick={handlePrint}>
                        Print / Save PDF
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShippingEvidenceDialog;
